'use client';

import * as React from 'react';
import { AnimatePresence, motion, useInView } from 'motion/react';

import { AccountMark, SourceMark } from './account-intelligence-scenes';
import {
  ACCOUNT_UPDATES,
  accountUpdateAt,
  MONITORING_CYCLE_MS,
  MONITORING_INTRO_MS,
  monitoringSequences,
  type AccountUpdate
} from './account-monitoring-data';

const FOLLOWED_ACCOUNTS = ['Cedarpath', 'Brightvault', 'Quillstack'] as const;
const ACCOUNT_PULSE = { opacity: [0, 0.55, 0], scale: [1, 1.25, 1.6] };
const STILL_PULSE = { opacity: 0, scale: 1 };
const CARD_SPRING = { type: 'spring' as const, stiffness: 210, damping: 25 };

function CompanyMark({ account }: { account: string }): React.JSX.Element {
  if (account === 'Quillstack') return <AccountMark />;
  return (
    <span
      aria-hidden
      className="grid size-8 shrink-0 place-items-center rounded-[9px] border border-[color:var(--hairline)] bg-[color:var(--paper-2)] font-mono text-[12px] font-medium text-[color:var(--ink-3)]"
    >
      {account[0]}
    </span>
  );
}

export function AccountMonitoringScene({
  reduced,
  ready,
  paused = false,
  advanceToken = 0
}: {
  reduced: boolean;
  ready: boolean;
  paused?: boolean;
  advanceToken?: number;
}): React.JSX.Element {
  const sectionRef = React.useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { amount: 0.35 });
  const [playback, setPlayback] = React.useState({ cursor: 0, arrived: false });
  const [pulseSequence, setPulseSequence] = React.useState<number | null>(null);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(true);
  const elapsed = React.useRef(0);
  const lastPulse = React.useRef<number | null>(null);
  const lastAdvance = React.useRef(advanceToken);
  const helpId = React.useId();
  const arrived = reduced || playback.arrived;
  const running =
    ready &&
    inView &&
    pageVisible &&
    !reduced &&
    !paused &&
    !hovered &&
    !focused;

  React.useEffect(() => {
    const update = (): void => setPageVisible(!document.hidden);
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  React.useEffect(() => {
    if (lastAdvance.current === advanceToken) return;
    lastAdvance.current = advanceToken;
    elapsed.current = 0;
    setPulseSequence(null);
    setPlayback((current) => ({
      cursor: current.arrived || reduced ? current.cursor + 1 : current.cursor,
      arrived: true
    }));
  }, [advanceToken, reduced]);

  // One elapsed clock drives the account pulse and arrival. Pausing preserves
  // reading time; hidden and offscreen time is never accumulated.
  React.useEffect(() => {
    if (!running) return;
    let frame = 0;
    let previous = performance.now();
    const duration = playback.arrived
      ? MONITORING_CYCLE_MS
      : MONITORING_INTRO_MS;
    const incoming = playback.arrived ? playback.cursor + 1 : playback.cursor;
    const tick = (now: number): void => {
      elapsed.current += Math.min(now - previous, 100);
      previous = now;
      if (elapsed.current >= duration - 900 && lastPulse.current !== incoming) {
        lastPulse.current = incoming;
        setPulseSequence(incoming);
      }
      if (elapsed.current >= duration) {
        elapsed.current = 0;
        setPulseSequence(null);
        setPlayback({ cursor: incoming, arrived: true });
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, playback]);

  return (
    <section
      ref={sectionRef}
      aria-label="Rotating account monitoring example"
      aria-describedby={helpId}
      data-account-monitoring
      data-monitoring-arrived={arrived}
      data-monitoring-running={running}
      data-monitoring-index={playback.cursor % ACCOUNT_UPDATES.length}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
      className="relative flex h-full min-h-0 items-center justify-center px-4 pb-4 pt-2 sm:px-8 md:px-10"
    >
      <p
        id={helpId}
        className="sr-only"
      >
        Twelve illustrative updates rotate every five seconds. Pause or browse
        with the controls above. Automatic updates pause while you hover over
        this preview.
      </p>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-35 [background-image:radial-gradient(circle,rgba(36,166,137,0.16)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
      />
      <div className="relative w-full max-w-[660px]">
        <div className="mb-4 flex h-8 items-center justify-between gap-2">
          <p className="text-[11px] font-medium tracking-[-0.01em] text-[color:var(--ink-3)] min-[375px]:text-[12px] md:text-[13px]">
            Following <span className="tabular-nums">3</span> accounts
          </p>
          <ul
            aria-label="Accounts followed: Cedarpath, Brightvault, Quillstack"
            className="flex shrink-0 -space-x-2"
          >
            {FOLLOWED_ACCOUNTS.map((account) => (
              <li
                key={account}
                className="relative rounded-[9px] ring-[3px] ring-[color:var(--paper)]"
              >
                {!reduced &&
                  pulseSequence !== null &&
                  accountUpdateAt(pulseSequence).account === account && (
                    <motion.span
                      key={pulseSequence}
                      aria-hidden
                      data-monitoring-pulse={account}
                      initial={STILL_PULSE}
                      animate={ACCOUNT_PULSE}
                      transition={{ duration: 0.9 }}
                      className="pointer-events-none absolute -inset-1 rounded-[12px] border border-[color:var(--accent-deep)] bg-[color:var(--mint)]"
                    />
                  )}
                <span className="relative block">
                  <CompanyMark account={account} />
                </span>
              </li>
            ))}
          </ul>
        </div>

        <ol
          aria-label="Account change notifications, newest first"
          aria-live="off"
          className="relative h-[412px] [--feature-height:210px] [--history-height:90px] min-[375px]:h-[358px] min-[375px]:[--feature-height:180px] min-[375px]:[--history-height:78px] md:h-[334px] md:[--feature-height:164px] md:[--history-height:74px]"
        >
          <AnimatePresence initial={false}>
            {monitoringSequences(playback.cursor, arrived).map(
              (sequence, index) => {
                const update = accountUpdateAt(sequence);
                const featured = arrived && index === 0;
                const historyIndex = arrived ? index - 1 : index;
                return (
                  <motion.li
                    key={sequence}
                    layout={reduced ? false : true}
                    data-account-notification={update.account}
                    data-notification-sequence={sequence}
                    data-notification-featured={featured}
                    initial={
                      reduced ? false : { opacity: 0, y: -24, scale: 0.97 }
                    }
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={
                      reduced
                        ? { opacity: 0, transition: { duration: 0 } }
                        : {
                            opacity: 0,
                            y: 14,
                            scale: 0.97,
                            transition: { duration: 0.22 }
                          }
                    }
                    transition={
                      reduced
                        ? { duration: 0 }
                        : {
                            ...CARD_SPRING,
                            opacity: { duration: 0.25 },
                            layout: CARD_SPRING
                          }
                    }
                    style={{
                      top: featured
                        ? 0
                        : 'calc(' +
                          (arrived ? 'var(--feature-height) + 12px' : '0px') +
                          ' + ' +
                          historyIndex +
                          ' * (var(--history-height) + 10px))',
                      height: featured
                        ? 'var(--feature-height)'
                        : 'var(--history-height)',
                      left: featured ? 0 : (historyIndex + 1) * 6,
                      right: featured ? 0 : (historyIndex + 1) * 6,
                      zIndex: 3 - index
                    }}
                    className={
                      'absolute overflow-hidden border bg-[color:var(--paper)] ' +
                      (featured
                        ? 'rounded-[16px] border-[color:var(--accent-deep)]/25 shadow-[0_5px_10px_rgba(0,62,47,0.035),0_18px_38px_-16px_rgba(0,62,47,0.19)]'
                        : 'rounded-[12px] border-[color:var(--hairline-2)] shadow-[0_5px_12px_rgba(0,62,47,0.035)]')
                    }
                  >
                    <motion.article
                      aria-hidden={!featured}
                      initial={false}
                      animate={{ opacity: featured ? 1 : 0 }}
                      transition={{ duration: reduced ? 0 : 0.16 }}
                      className="absolute inset-0 flex flex-col items-start px-3.5 py-3.5 min-[375px]:px-4 md:px-5 md:py-4"
                    >
                      <div className="flex w-full items-center gap-2.5">
                        <CompanyMark account={update.account} />
                        <p className="text-[12px] font-semibold tracking-[-0.015em] text-[color:var(--ink)] md:text-[13px]">
                          {update.account}
                        </p>
                      </div>
                      <h3 className="mt-2.5 text-[20px] font-semibold leading-[1.2] tracking-[-0.035em] text-[color:var(--ink)] md:mt-2 md:text-[23px]">
                        <motion.mark
                          initial={
                            reduced ? false : { backgroundSize: '0% 100%' }
                          }
                          animate={{ backgroundSize: '100% 100%' }}
                          style={{
                            backgroundImage:
                              'linear-gradient(var(--mint), var(--mint))',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'left center'
                          }}
                          transition={{
                            duration: reduced ? 0 : 0.7,
                            delay: reduced ? 0 : 0.4,
                            ease: [0.22, 1, 0.36, 1]
                          }}
                          className="bg-transparent px-0.5 text-inherit box-decoration-clone"
                        >
                          {update.change}
                        </motion.mark>
                      </h3>
                      <EvidenceLine update={update} />
                      <p className="mt-auto pt-2 text-[12px] font-medium leading-snug tracking-[-0.01em] text-[color:var(--ink-3)] md:text-[13px]">
                        <span className="text-[color:var(--accent-darker)]">
                          Next move:
                        </span>{' '}
                        {update.action}
                      </p>
                    </motion.article>
                    <motion.article
                      aria-hidden={featured}
                      initial={false}
                      animate={{ opacity: featured ? 0 : 1 }}
                      transition={{
                        duration: reduced ? 0 : 0.2,
                        delay: reduced || featured ? 0 : 0.1
                      }}
                      className="absolute inset-0 flex gap-2.5 px-3 py-2.5 md:px-4"
                    >
                      <span className="mt-0.5 [&>span]:size-7 [&>span]:rounded-[8px] [&>span]:text-[10px]">
                        <CompanyMark account={update.account} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium leading-tight text-[color:var(--ink-4)] md:text-[11px]">
                          {update.account}
                        </p>
                        <p className="mt-1 text-[12px] font-medium leading-snug tracking-[-0.01em] text-[color:var(--ink-3)] md:text-[13px]">
                          {update.change}
                        </p>
                        <EvidenceLine
                          update={update}
                          compact
                        />
                      </div>
                    </motion.article>
                  </motion.li>
                );
              }
            )}
          </AnimatePresence>
        </ol>
      </div>
    </section>
  );
}

function EvidenceLine({
  update,
  compact = false
}: {
  update: AccountUpdate;
  compact?: boolean;
}): React.JSX.Element {
  return (
    <p
      className={
        'flex items-center gap-1 text-[9px] leading-snug text-[color:var(--ink-4)] ' +
        (compact ? 'mt-1' : 'mt-1.5 min-[375px]:text-[10px]')
      }
    >
      <span
        aria-hidden
        className={
          'shrink-0 text-[color:var(--accent-deep)]/70 ' +
          (compact ? '[&_svg]:size-2.5' : '[&_svg]:size-3')
        }
      >
        <SourceMark index={update.sourceMark} />
      </span>
      <span>
        {compact ? update.compactSource : update.source}{' '}
        <span aria-hidden>·</span>{' '}
        <time
          dateTime={'2026-09-' + update.date.slice(-2)}
          className="whitespace-nowrap tabular-nums"
        >
          {update.date}
        </time>
      </span>
    </p>
  );
}
